import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { fmtDate, fmtCurrency, StatusBadge } from "../lib/status";
import { ShieldCheck, CheckCircle2, MessageSquareWarning, AlertTriangle, Mail, Phone, ClipboardList, HelpCircle, XCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { VehicleDamageMap } from "../components/VehicleDamageMap";
import { VIEW_OPTIONS, tagByCode, SEVERITY_DOT_CLASS, detectVehicleType, defaultViewsForType } from "../lib/damages";
import NoIndex from "../components/NoIndex";
import RenterVehicleDetailModal from "../components/vehicle-details/RenterVehicleDetailModal";
import BookingConditionRecordModal from "../components/condition-record/BookingConditionRecordModal";
import CustomerDriversCard from "../components/renter-drivers/CustomerDriversCard";
import CustomerSettlementCard from "../components/rental-lifecycle/CustomerSettlementCard";

// Structured "Request a Change" categories (gap-closure item 2): keys match
// public/schemas.py's ALLOWED_CHANGE_TYPES exactly. The freeform note below
// stays available either way - selecting none is still a valid submission.
const CHANGE_TYPE_OPTIONS = [
  { key: "pickup_date", label: "Pickup Date" },
  { key: "pickup_time", label: "Pickup Time" },
  { key: "return_date", label: "Return Date" },
  { key: "return_time", label: "Return Time" },
  { key: "vehicle", label: "Vehicle" },
  { key: "pickup_location", label: "Pickup Location" },
  { key: "return_location", label: "Return Location" },
  { key: "other", label: "Other" },
];

function Field({ label, value, onChange, testId, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-800">{label}</span>
      <input
        type={type} value={value} onChange={onChange} data-testid={testId} placeholder={placeholder}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-amber-300 bg-white focus:border-amber-500 outline-none text-sm"
      />
    </label>
  );
}

/**
 * `preloaded`/`onAction`: optional sandboxed-demo hooks (used by the
 * homepage walkthrough), matching the same pattern already established on
 * RentalProfile. When `preloaded` is provided, the real `/public/confirm/:token`
 * fetch is skipped and `onAction` is called instead of the real POST for
 * confirm/request-change: the walkthrough decides how to simulate the
 * resulting state. Real behavior (real fetch, real POST) is unchanged when
 * these props are absent.
 */
export default function CustomerConfirmation({ preloaded = null, onAction = null, onViewVehicleDetails = null } = {}) {
  const { token } = useParams();
  const [data, setData] = useState(preloaded);
  const [loading, setLoading] = useState(!preloaded);
  const [acting, setActing] = useState(false);
  const [showChange, setShowChange] = useState(false);
  const [changeTypes, setChangeTypes] = useState(() => new Set());
  const [changeFields, setChangeFields] = useState({
    pickup_date: "", pickup_time: "", return_date: "", return_time: "",
    pickup_location: "", return_location: "", vehicle_note: "",
  });
  const [message, setMessage] = useState("");
  const [completed, setCompleted] = useState(null);
  const [damageView, setDamageView] = useState("front");
  const [ackBusy, setAckBusy] = useState(false);
  const [flagged, setFlagged] = useState(new Set());
  const [customerNote, setCustomerNote] = useState("");
  const [termsAcknowledged, setTermsAcknowledged] = useState(false);
  // Real usage (no `onViewVehicleDetails` prop) opens our own modal fetching
  // the actual booked vehicle via the token; the walkthrough demo supplies
  // its own handler and stays untouched.
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const handleViewVehicle = onViewVehicleDetails || (() => setShowVehicleModal(true));
  const [showRecordModal, setShowRecordModal] = useState(false);
  // Exception-flow gap closure item 4: the backend deliberately unifies
  // every invalid/expired/used/revoked/superseded token into one 410 (kept
  // exactly as-is - it's what stops a bad link being used to probe whether
  // a booking exists). This only distinguishes that ONE response shape on
  // the frontend so the page shows an actual screen instead of silently
  // rendering blank (`if (!data) return null`) when `load()`'s catch used
  // to just toast and stop.
  const [linkInactive, setLinkInactive] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/public/confirm/${token}`);
      setData(data);
      // Pick the first view that actually has damages so the customer
      // immediately sees a marker without hunting through tabs.
      const firstWithDamage = (data.damages || []).reduce((acc, d) => acc || d.view, null);
      if (firstWithDamage) setDamageView(firstWithDamage);
      // Seed flagged set with prior selections (if customer revisits)
      const prior = data.booking?.damages_flagged_by_customer || [];
      if (prior.length) setFlagged(new Set(prior));
      if (data.booking?.damage_customer_message) setCustomerNote(data.booking.damage_customer_message);
    } catch (err) {
      if (err?.response?.status === 410) {
        setLinkInactive(true);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!preloaded) load(); }, [token]);

  const changeRequestPayload = () => {
    const types = Array.from(changeTypes);
    const payload = { message: message || undefined, change_types: types };
    if (types.includes("pickup_date")) payload.requested_pickup_date = changeFields.pickup_date || undefined;
    if (types.includes("pickup_time")) payload.requested_pickup_time = changeFields.pickup_time || undefined;
    if (types.includes("return_date")) payload.requested_return_date = changeFields.return_date || undefined;
    if (types.includes("return_time")) payload.requested_return_time = changeFields.return_time || undefined;
    if (types.includes("pickup_location")) payload.requested_pickup_location = changeFields.pickup_location || undefined;
    if (types.includes("return_location")) payload.requested_return_location = changeFields.return_location || undefined;
    if (types.includes("vehicle")) payload.requested_vehicle_note = changeFields.vehicle_note || undefined;
    return payload;
  };

  const act = async (action) => {
    setActing(true);
    if (onAction) {
      const res = onAction(action, action === "request_change"
        ? { ...changeRequestPayload(), terms_acknowledged: termsAcknowledged }
        : { terms_acknowledged: termsAcknowledged });
      setCompleted(res?.status || (action === "confirm" ? "confirmed" : "change_requested"));
      toast.success(action === "confirm" ? "Booking confirmed!" : "Change request sent");
      setActing(false);
      return;
    }
    try {
      const { data: res } = await api.post(`/public/confirm/${token}`, {
        action,
        terms_acknowledged: termsAcknowledged,
        ...(action === "request_change" ? changeRequestPayload() : {}),
      });
      setCompleted(res.status);
      toast.success(action === "confirm" ? "Booking confirmed!" : "Change request sent");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActing(false);
    }
  };

  const cancelRequest = async () => {
    if (!window.confirm("Cancel this booking request? This can't be undone.")) return;
    setActing(true);
    if (onAction) {
      const res = onAction("cancel_request", {});
      setCompleted(res?.status || "cancelled");
      toast.success("Booking request cancelled");
      setActing(false);
      return;
    }
    try {
      const { data: res } = await api.post(`/public/confirm/${token}/cancel`, {});
      setCompleted(res.status);
      toast.success("Booking request cancelled");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActing(false);
    }
  };

  const toggleChangeType = (key) => {
    setChangeTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const setChangeField = (key) => (e) => setChangeFields((f) => ({ ...f, [key]: e.target.value }));

  const toggleFlag = (id) => {
    setFlagged((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const acknowledgeDamages = async () => {
    setAckBusy(true);
    const flaggedIds = Array.from(flagged);
    if (onAction) {
      const res = onAction("acknowledge_damages", { flagged_damage_ids: flaggedIds, customer_message: customerNote.trim() || null }) || {};
      setData((cur) => cur ? {
        ...cur,
        booking: {
          ...cur.booking,
          damages_acknowledged_at: res.damages_acknowledged_at || new Date().toISOString(),
          damages_acknowledged_by: res.damages_acknowledged_by || cur.booking.customer_name,
          damages_flagged_by_customer: flaggedIds,
          damage_customer_message: customerNote.trim() || null,
        },
      } : cur);
      toast.success(flaggedIds.length > 0 ? "Acknowledged. Your clarification request was sent to the rental business." : "Thanks: damage list acknowledged.");
      setAckBusy(false);
      return;
    }
    try {
      const { data: res } = await api.post(`/public/confirm/${token}/acknowledge-damages`, {
        signature_name: data?.booking?.customer_name || "",
        flagged_damage_ids: flaggedIds,
        customer_message: customerNote.trim() || null,
      });
      setData((cur) => cur ? {
        ...cur,
        booking: {
          ...cur.booking,
          damages_acknowledged_at: res.damages_acknowledged_at,
          damages_acknowledged_by: res.damages_acknowledged_by,
          damages_flagged_by_customer: res.damages_flagged_by_customer || flaggedIds,
          damage_customer_message: res.damage_customer_message,
        },
      } : cur);
      if (res.already_acknowledged) {
        toast.success("Already acknowledged");
      } else if (flaggedIds.length > 0) {
        toast.success("Acknowledged. Your clarification request was sent to the rental business.");
      } else {
        toast.success("Thanks: damage list acknowledged.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAckBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading your booking…</div>;
  if (linkInactive) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] grid place-items-center px-4" data-testid="link-inactive-page">
        <NoIndex title="Link no longer active · ConfirmPH" />
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 grid place-items-center mx-auto">
            <XCircle className="w-6 h-6" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900">This link is no longer active</h1>
          <p className="text-sm text-slate-600" data-testid="link-inactive-message">
            This confirmation link is no longer active. It may have expired or been replaced.
            Please contact the rental company for the latest booking details.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Link to="/find-booking"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#163829]"
                  data-testid="link-inactive-contact-company">
              Contact Rental Company
            </Link>
            <Link to="/rentals"
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
                  data-testid="link-inactive-view-company">
              View Rental Company
            </Link>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { booking, vehicle, company, expired, token_status, terms } = data;
  const damages = data.damages || [];
  const termsGateBlocked = !!terms && !termsAcknowledged;
  const ackAt = booking.damages_acknowledged_at;
  const ackBy = booking.damages_acknowledged_by;
  const vehicleType = detectVehicleType(vehicle?.category);
  const customerViews = defaultViewsForType(vehicleType);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="customer-confirmation-page">
      <NoIndex title="Confirm your booking · ConfirmPH" />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#1B4332] text-white grid place-items-center">
            <ShieldCheck className="w-4 h-4" strokeWidth={2.2} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-semibold">ConfirmPH</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Secure confirmation</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6">
        {completed && (
          <div className={`rounded-2xl p-6 border ${
            completed === "confirmed" || completed === "extension_confirmed" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : completed === "cancelled" ? "bg-slate-100 border-slate-300 text-slate-900"
            : "bg-amber-50 border-amber-200 text-amber-900"
          }`} data-testid="completed-banner">
            {completed === "confirmed" ? (
              <>
                <CheckCircle2 className="w-8 h-8" />
                <h2 className="mt-2 font-display text-xl font-semibold">Booking confirmed!</h2>
                <p className="mt-1 text-sm">Thanks for confirming. {company?.name} has been notified.</p>
              </>
            ) : completed === "extension_confirmed" ? (
              <>
                <CheckCircle2 className="w-8 h-8" />
                <h2 className="mt-2 font-display text-xl font-semibold">Extension confirmed!</h2>
                <p className="mt-1 text-sm">Your updated return date is on record. {company?.name} has been notified.</p>
              </>
            ) : completed === "cancelled" ? (
              <>
                <XCircle className="w-8 h-8" />
                <h2 className="mt-2 font-display text-xl font-semibold">Booking Request Cancelled</h2>
                <p className="mt-1 text-sm">Your request has been cancelled. {company?.name} has been informed.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {company?.email && (
                    <a href={`mailto:${company.email}`} data-testid="cancelled-contact-company"
                       className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Contact Rental Company
                    </a>
                  )}
                  <Link to="/" data-testid="cancelled-view-company"
                        className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium">
                    View Rental Company
                  </Link>
                </div>
              </>
            ) : (
              <>
                <MessageSquareWarning className="w-8 h-8" />
                <h2 className="mt-2 font-display text-xl font-semibold">Change request sent</h2>
                <p className="mt-1 text-sm">{company?.name} will review and follow up with you.</p>
              </>
            )}
          </div>
        )}

        {expired && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900 text-sm" data-testid="expired-banner">
            <AlertTriangle className="w-5 h-5 inline -mt-1 mr-1" />
            This confirmation link has expired. Please contact {company?.name} for an updated link.
          </div>
        )}
        {token_status !== "active" && !expired && !completed && booking?.status !== "confirmed" && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            This link is no longer active. It may have been used or replaced.
          </div>
        )}

        <EnhancedSafetyNotice company={company} vehicleSafetyHold={data.vehicle_safety_hold} />

        <ExtensionPendingCard
          token={token}
          booking={booking}
          tokenStatus={token_status}
          expired={expired}
          onAction={onAction}
          onConfirmed={(newEnd) => {
            setCompleted("extension_confirmed");
            if (!preloaded) load();
          }}
        />

        <PendingChangeCard
          token={token}
          versionDiff={data.version_diff}
          bookingStatus={booking.status}
          tokenStatus={token_status}
          expired={expired}
          termsAcknowledged={termsAcknowledged}
          termsGateBlocked={termsGateBlocked}
          onAction={onAction}
          onAccepted={() => { setCompleted("confirmed"); if (!preloaded) load(); }}
          onClarificationRequested={() => { setCompleted("change_requested"); if (!preloaded) load(); }}
        />

        <CustomerTimelineCard token={token} disabled={!!preloaded} />

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">From</div>
          <h1 className="font-display text-3xl font-bold mt-1">{company?.name}</h1>
          {company?.city && <p className="text-sm text-slate-600">{company.city}</p>}
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
            {company?.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {company.email}</span>}
            {company?.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {company.phone}</span>}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Your booking</div>
              <div className="font-mono text-sm text-slate-700 mt-1">{booking.booking_reference}</div>
            </div>
            <StatusBadge status={booking.status} />
          </div>

          {vehicle && (
            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Vehicle</div>
                  <div className="mt-1 font-display text-xl font-semibold">{vehicle.name}</div>
                  <div className="text-sm text-slate-600">
                    {vehicle.category} · {vehicle.transmission} · {vehicle.seats} seats
                    {vehicle.plate_number ? ` · Plate ${vehicle.plate_number}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleViewVehicle}
                  data-testid="confirmation-view-vehicle-btn"
                  className="text-xs font-medium px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 whitespace-nowrap"
                >
                  View vehicle details →
                </button>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
            <Item label="Pickup" value={fmtDate(booking.start_datetime)} sub={booking.pickup_location} />
            <Item label="Return" value={fmtDate(booking.end_datetime)} sub={booking.return_location} />
            <Item label="Vehicle rental price" value={fmtCurrency(booking.price)} mono />
            <Item label="Deposit required" value={fmtCurrency(booking.deposit_required)} mono />
            {booking.rental_service_type === "with_driver" && (
              <>
                <Item label="Service type" value="With driver" />
                <Item label="Driver service"
                      value={booking.driver_service?.quote_required
                        ? "To be confirmed by the business"
                        : booking.driver_service_price ? fmtCurrency(booking.driver_service_price) : "-"}
                      sub={booking.driver_display_name
                        ? `Driver: ${booking.driver_display_name}`
                        : (booking.driver_service?.assignment_note
                          || (booking.driver_assignment_status === "pending" ? "Driver to be assigned" : undefined))}
                      mono />
                {booking.driver_service?.passenger_count ? (
                  <Item label="Passengers" value={String(booking.driver_service.passenger_count)}
                        sub={booking.driver_service.destination || undefined} />
                ) : null}
              </>
            )}
            <Item label="Name on booking" value={booking.customer_name} />
            <Item label="Email" value={booking.customer_email} />
          </div>

          {booking.customer_visible_notes && (
            <div className="border-t border-slate-100 pt-4">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Important notes</div>
              <p className="mt-1 text-sm whitespace-pre-line">{booking.customer_visible_notes}</p>
            </div>
          )}

          {!preloaded && (booking.rental_service_type || "self_drive") === "self_drive" && (
            <CustomerDriversCard basePath={`/public/confirm/${token}`} renterName={booking.customer_name} />
          )}

          {/* Task 7: the after-the-rental account. Renders nothing until there
              is something to show, and applies to with-driver rentals too. */}
          {!preloaded && (
            <CustomerSettlementCard basePath={`/public/confirm/${token}/rental-settlement`} />
          )}

          <div className="border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={() => setShowRecordModal(true)}
              data-testid="confirmation-view-record-btn"
              className="text-xs font-medium text-slate-600 hover:text-slate-900 hover:underline"
            >
              View Booking &amp; Condition Record →
            </button>
          </div>
        </div>

        {terms && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="customer-terms-section">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold inline-flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Terms acknowledgment
              </div>
              <h2 className="font-display text-lg font-semibold mt-1">Booking terms: Version {terms.version_number}</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Please review the rental terms below before confirming.
              </p>
            </div>
            <div className="divide-y divide-slate-100">
              {TERMS_SECTION_LABELS.filter((s) => (terms.sections?.[s.key] || "").trim()).map((s) => (
                <div key={s.key} className="py-2.5 text-sm" data-testid={`customer-terms-${s.key}`}>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{s.label}</div>
                  <p className="mt-1 whitespace-pre-line text-slate-700">{terms.sections[s.key]}</p>
                </div>
              ))}
            </div>
            {!completed && !expired && token_status === "active" && (
              <label className="flex items-start gap-2 text-sm rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer"
                     data-testid="terms-acknowledge-checkbox-label">
                <input type="checkbox" checked={termsAcknowledged}
                       onChange={(e) => setTermsAcknowledged(e.target.checked)}
                       data-testid="terms-acknowledge-checkbox"
                       className="mt-0.5" />
                <span>
                  I have reviewed and agree to the booking details and rental terms provided by {company?.name || "the rental business"}.
                </span>
              </label>
            )}
          </div>
        )}

        {damages.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="customer-damage-sheet">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold inline-flex items-center gap-1">
                  <ClipboardList className="w-3.5 h-3.5" /> Existing vehicle damage
                </div>
                <h2 className="font-display text-lg font-semibold mt-1">
                  Damage sheet ({damages.length} item{damages.length === 1 ? "" : "s"})
                </h2>
                <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                  Please review the pre-existing damage on this vehicle before pickup. New damage that appears
                  during your rental will be added on top of this list at return. If you don't see any of these
                  on the actual vehicle, tap "I don't see this": the rental business will be notified for clarification.
                </p>
              </div>
              {ackAt && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 inline-flex items-center gap-1" data-testid="damage-acknowledged-badge">
                  <CheckCircle2 className="w-3 h-3" /> Acknowledged
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100 w-fit" data-testid="customer-damage-view-tabs">
              {customerViews.map((v) => {
                const count = damages.filter((d) => d.view === v.value).length;
                if (count === 0) return null;
                return (
                  <button
                    key={v.value}
                    onClick={() => setDamageView(v.value)}
                    data-testid={`customer-damage-view-${v.value}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      damageView === v.value ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {v.label} <span className="text-[10px] text-slate-500">({count})</span>
                  </button>
                );
              })}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 sm:p-4">
              <VehicleDamageMap
                vehicleType={vehicleType}
                view={damageView}
                damages={damages}
                readOnly
              />
            </div>

            <ul className="divide-y divide-slate-100 -mx-2" data-testid="customer-damage-list">
              {damages.map((d, i) => {
                const tag = tagByCode(d.tag_code);
                const isFlagged = flagged.has(d.id);
                const lockedAfterAck = !!ackAt;
                return (
                  <li key={d.id} className="px-2 py-2.5 text-sm" data-testid={`customer-damage-item-${d.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold">{i + 1}</span>
                        <span className="font-medium">
                          {d.tag_code === "other" ? (d.tag_label || tag.label) : tag.label}
                        </span>
                        <span className={`inline-block w-2 h-2 rounded-full ${SEVERITY_DOT_CLASS[d.severity] || "bg-slate-400"}`} />
                        <span className="text-xs text-slate-500 uppercase tracking-wider">{d.severity}</span>
                        <span className="text-xs text-slate-500">· {VIEW_OPTIONS.find((v) => v.value === d.view)?.label || d.view}</span>
                        {isFlagged && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200 inline-flex items-center gap-1"
                            data-testid={`customer-damage-flagged-${d.id}`}
                          >
                            <HelpCircle className="w-3 h-3" /> Needs clarification
                          </span>
                        )}
                      </div>
                      {!lockedAfterAck && (
                        <button
                          type="button"
                          onClick={() => toggleFlag(d.id)}
                          data-testid={`customer-damage-flag-toggle-${d.id}`}
                          className={`text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 transition ${
                            isFlagged
                              ? "border-amber-300 bg-amber-50 text-amber-900"
                              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          {isFlagged ? (<><XCircle className="w-3 h-3" /> Cancel flag</>) : (<><HelpCircle className="w-3 h-3" /> I don't see this</>)}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            {!ackAt && flagged.size > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" data-testid="customer-damage-flagged-notice">
                <p>
                  <b>{flagged.size} item{flagged.size === 1 ? "" : "s"} flagged.</b> When you tap "Acknowledge" below,
                  we'll let the rental business know you'd like clarification on these. Your booking flow continues
                  normally in the meantime.
                </p>
                <label className="block">
                  <span className="block font-medium mb-1">Optional message to the rental business</span>
                  <textarea
                    value={customerNote}
                    onChange={(e) => setCustomerNote(e.target.value.slice(0, 1000))}
                    rows={2}
                    maxLength={1000}
                    placeholder="e.g. The scratch on the back left doesn't match what's on the car."
                    data-testid="customer-damage-clarification-message"
                    className="w-full px-2.5 py-2 rounded-lg border border-amber-300 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                  />
                </label>
              </div>
            )}

            {!ackAt ? (
              <button
                onClick={acknowledgeDamages}
                disabled={ackBusy}
                data-testid="acknowledge-damages-btn"
                className="w-full px-5 py-3 rounded-xl border-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <CheckCircle2 className="w-4 h-4" />
                {ackBusy ? "Saving…" : (
                  flagged.size > 0
                    ? `Acknowledge ${damages.length - flagged.size} damage(s) · request clarification on ${flagged.size}`
                    : "I've reviewed the existing damage: acknowledge"
                )}
              </button>
            ) : (
              <div className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 space-y-1" data-testid="customer-damage-ack-confirm">
                <p>Acknowledged by <b>{ackBy || booking.customer_name}</b> on {fmtDate(ackAt)}.</p>
                {(booking.damages_flagged_by_customer || []).length > 0 && (
                  <p className="text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 inline-flex items-start gap-1">
                    <HelpCircle className="w-3.5 h-3.5 mt-0.5" />
                    <span>
                      Your clarification request on {(booking.damages_flagged_by_customer || []).length} item(s)
                      was sent to the rental business.
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {!completed && !expired && token_status === "active" && (
          <div className="space-y-3" data-testid="confirm-actions">
            {!showChange ? (
              <>
                <button
                  onClick={() => act("confirm")}
                  disabled={acting || termsGateBlocked}
                  data-testid="confirm-booking-btn"
                  title={termsGateBlocked ? "Please acknowledge the booking terms above first" : ""}
                  className="w-full px-5 py-4 rounded-xl btn-brand font-semibold text-base inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <CheckCircle2 className="w-5 h-5" /> Confirm this booking
                </button>
                <button
                  onClick={() => setShowChange(true)}
                  data-testid="request-change-btn"
                  className="w-full px-5 py-4 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-base inline-flex items-center justify-center gap-2"
                >
                  <MessageSquareWarning className="w-5 h-5" /> Request a correction
                </button>
                {booking.status === "awaiting_customer_confirmation" && (
                  <button
                    onClick={cancelRequest}
                    disabled={acting}
                    data-testid="cancel-request-btn"
                    className="w-full px-5 py-3 rounded-xl text-rose-700 hover:bg-rose-50 font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    <XCircle className="w-4 h-4" /> Cancel Request
                  </button>
                )}
              </>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-800">What would you like to change?</span>
                  <div className="mt-2 grid grid-cols-2 gap-1.5">
                    {CHANGE_TYPE_OPTIONS.map((opt) => (
                      <label key={opt.key} className="inline-flex items-center gap-1.5 text-sm text-amber-900" data-testid={`change-type-${opt.key}`}>
                        <input type="checkbox" checked={changeTypes.has(opt.key)} onChange={() => toggleChangeType(opt.key)}
                               className="rounded border-amber-400 text-[#1B4332] focus:ring-[#1B4332]" />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {changeTypes.has("pickup_date") && (
                  <Field label="Requested pickup date" testId="change-pickup-date" type="date" value={changeFields.pickup_date} onChange={setChangeField("pickup_date")} />
                )}
                {changeTypes.has("pickup_time") && (
                  <Field label="Requested pickup time" testId="change-pickup-time" type="time" value={changeFields.pickup_time} onChange={setChangeField("pickup_time")} />
                )}
                {changeTypes.has("return_date") && (
                  <Field label="Requested return date" testId="change-return-date" type="date" value={changeFields.return_date} onChange={setChangeField("return_date")} />
                )}
                {changeTypes.has("return_time") && (
                  <Field label="Requested return time" testId="change-return-time" type="time" value={changeFields.return_time} onChange={setChangeField("return_time")} />
                )}
                {changeTypes.has("pickup_location") && (
                  <Field label="Requested pickup location" testId="change-pickup-location" value={changeFields.pickup_location} onChange={setChangeField("pickup_location")} />
                )}
                {changeTypes.has("return_location") && (
                  <Field label="Requested return location" testId="change-return-location" value={changeFields.return_location} onChange={setChangeField("return_location")} />
                )}
                {changeTypes.has("vehicle") && (
                  <Field label="What vehicle would you prefer?" testId="change-vehicle-note" value={changeFields.vehicle_note} onChange={setChangeField("vehicle_note")}
                         placeholder="e.g. I'd like a different vehicle if available" />
                )}

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-800">Anything else we should know? {changeTypes.size > 0 ? "(optional)" : ""}</span>
                  <textarea
                    data-testid="change-message-input"
                    value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                    placeholder="e.g. If possible."
                    className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-amber-300 bg-white focus:border-amber-500 outline-none"
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => act("request_change")} disabled={acting} data-testid="send-change-btn" className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60">
                    Send change request
                  </button>
                  <button onClick={() => setShowChange(false)} className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50">
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="text-center pt-4">
          <Link to="/" className="text-xs text-slate-500 hover:underline">Powered by ConfirmPH</Link>
        </div>
      </main>

      {!onViewVehicleDetails && showVehicleModal && (
        <RenterVehicleDetailModal source={{ mode: "token", token }} onClose={() => setShowVehicleModal(false)} />
      )}

      {showRecordModal && (
        <BookingConditionRecordModal source={{ mode: "token", token }} onClose={() => setShowRecordModal(false)} />
      )}
    </div>
  );
}

function Item({ label, value, sub, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
      {sub && <div className="text-sm text-slate-600">{sub}</div>}
    </div>
  );
}


// -----------------------------------------------------------------------
// Pending change card: old vs new + Accept / Request clarification.
// Driven by the generic version_diff computed on the backend (Phase 2C)
// instead of a hand-built vehicle-only pending_customer_change object, so
// it now renders whichever material fields actually changed: vehicle,
// dates, price, deposit, locations, or notes.
// -----------------------------------------------------------------------
const TERMS_SECTION_LABELS = [
  { key: "deposit_policy", label: "Deposit policy" },
  { key: "cancellation_policy", label: "Cancellation policy" },
  { key: "late_return_policy", label: "Late return policy" },
  { key: "fuel_policy", label: "Fuel/charge policy" },
  { key: "damage_policy", label: "Damage policy" },
  { key: "travel_restrictions", label: "Travel restrictions" },
  { key: "other_terms", label: "Other rental terms" },
];

const DISPLAY_FIELDS = [
  { key: "vehicle", triggers: ["item_id", "vehicle_name"], label: "vehicle",
    get: (s) => s?.vehicle_name || "-" },
  { key: "start_datetime", triggers: ["start_datetime"], label: "pickup date",
    get: (s) => fmtDate(s?.start_datetime) },
  { key: "end_datetime", triggers: ["end_datetime"], label: "return date",
    get: (s) => fmtDate(s?.end_datetime) },
  { key: "pickup_location", triggers: ["pickup_location", "pickup_location_data"], label: "pickup location",
    get: (s) => s?.pickup_location || s?.pickup_location_data?.label || "-" },
  { key: "return_location", triggers: ["return_location", "return_location_data"], label: "return location",
    get: (s) => s?.return_location || s?.return_location_data?.label || "-" },
  { key: "price", triggers: ["price"], label: "price",
    get: (s) => fmtCurrency(s?.price) },
  { key: "deposit_required", triggers: ["deposit_required"], label: "deposit required",
    get: (s) => fmtCurrency(s?.deposit_required) },
  { key: "delivery_required", triggers: ["delivery_required"], label: "delivery",
    get: (s) => (s?.delivery_required ? "Yes" : "No") },
  { key: "return_same_as_pickup", triggers: ["return_same_as_pickup"], label: "return same as pickup",
    get: (s) => (s?.return_same_as_pickup ? "Yes" : "No") },
  { key: "customer_visible_notes", triggers: ["customer_visible_notes"], label: "notes",
    get: (s) => s?.customer_visible_notes || "-" },
];

/**
 * Enhanced Vehicle Safety Enforcement: three variants, in priority order.
 * 1. The assigned vehicle has an active Safety Hold - a blocking major
 *    notice, replaces the ordinary positive/neutral disclosure entirely.
 * 2. The business has Enhanced Safety active - a positive, factual
 *    indicator (green).
 * 3. The business has not enabled it - a neutral, non-alarming disclosure
 *    (amber, never red: not enabling it is not evidence of anything wrong).
 * Never blocks Confirm/Request a Change/Cancel Request when the business
 * simply hasn't enabled the program - only an actual vehicle-level hold
 * blocks anything, and even then only handover, not the confirm action
 * itself (confirming doesn't hand the customer a vehicle).
 */
function EnhancedSafetyNotice({ company, vehicleSafetyHold }) {
  if (vehicleSafetyHold?.active) {
    return (
      <div className="rounded-2xl border-2 border-red-400 bg-red-50 p-5" data-testid="vehicle-safety-hold-notice">
        <div className="font-display text-lg font-semibold text-red-900">IMPORTANT VEHICLE NOTICE</div>
        <p className="text-sm text-red-900/90 mt-1">
          The vehicle currently assigned to this booking cannot complete a ConfirmPH handover while its
          serious safety restriction remains unresolved.
        </p>
        <p className="text-sm text-red-900/80 mt-2">
          Please contact the rental company for next steps, or check back for an update.
        </p>
      </div>
    );
  }
  if (!company) return null;
  if (company.enhanced_safety_active) {
    return (
      <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5" data-testid="enhanced-safety-active-notice">
        <div className="font-display text-base font-semibold text-emerald-900">✓ Enhanced Safety Enforcement enabled</div>
        <p className="text-sm text-emerald-900/80 mt-1">
          This rental business has voluntarily enabled stronger ConfirmPH vehicle controls. Serious unresolved
          vehicle issues may prevent an affected vehicle from completing ConfirmPH handover.{" "}
          <Link to="/enhanced-safety" target="_blank" className="underline">Learn More</Link>
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5" data-testid="enhanced-safety-off-notice">
      <div className="font-display text-base font-semibold text-amber-900">Enhanced Safety Enforcement not enabled</div>
      <p className="text-sm text-amber-900/80 mt-1">
        This rental business has not yet enabled ConfirmPH's Enhanced Vehicle Safety Enforcement.
        Participating businesses accept stronger ConfirmPH controls for serious vehicle safety, document,
        inspection and vehicle-integrity issues.{" "}
        <Link to="/enhanced-safety" target="_blank" className="underline">Learn More</Link>
      </p>
    </div>
  );
}

/**
 * Gap-closure item 3: the renter-facing half of a staff-proposed rental
 * extension. Deliberately a small, separate card from PendingChangeCard
 * above (not the same reconfirmation machinery): an extension happens
 * while the booking is `ongoing`, not `awaiting_customer_confirmation`, so
 * it needs its own minimal accept action rather than reusing accept-changes.
 */
function ExtensionPendingCard({ token, booking, tokenStatus, expired, onAction, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  if (!booking.extension_pending_confirmation || !booking.pending_extension_end_datetime) return null;
  const isPending = tokenStatus === "active" && !expired;

  const confirm = async () => {
    setBusy(true);
    try {
      if (onAction) onAction("confirm_extension", {});
      else await api.post(`/public/confirm/${token}/accept-extension`, {});
      toast.success("Extension confirmed");
      onConfirmed?.(booking.pending_extension_end_datetime);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5" data-testid="extension-pending-card">
      <div className="font-display text-lg font-semibold text-amber-900">
        Your rental company proposed an extension
      </div>
      <p className="text-sm text-amber-900/80 mt-1">Please review the new return date before it's final.</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="extension-current-return">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Current return</div>
          <div className="font-medium mt-0.5">{fmtDate(booking.end_datetime)}</div>
        </div>
        <div className="rounded-lg border-2 border-[#1B4332] bg-white p-3" data-testid="extension-new-return">
          <div className="text-[10px] uppercase tracking-widest text-[#1B4332]">New return</div>
          <div className="font-medium mt-0.5">{fmtDate(booking.pending_extension_end_datetime)}</div>
        </div>
      </div>
      {booking.pending_extension_note && (
        <p className="mt-3 text-sm text-amber-900/80">Note from the rental company: {booking.pending_extension_note}</p>
      )}
      {isPending && (
        <div className="mt-4">
          <button onClick={confirm} disabled={busy} data-testid="confirm-extension-btn"
                  className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm hover:opacity-90 disabled:opacity-50">
            Confirm Extension
          </button>
        </div>
      )}
    </div>
  );
}

function PendingChangeCard({ token, versionDiff, bookingStatus, tokenStatus, expired,
                              termsAcknowledged, termsGateBlocked, onAction, onAccepted, onClarificationRequested }) {
  const [busy, setBusy] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  const [msg, setMsg] = useState("");

  const changedFields = versionDiff?.changed_fields || [];
  const hasPreviousConfirmed = !!versionDiff?.previous_confirmed_snapshot;
  if (!hasPreviousConfirmed || changedFields.length === 0) return null;

  const rows = DISPLAY_FIELDS.filter((f) => f.triggers.some((t) => changedFields.includes(t)));
  const termsChanged = changedFields.includes("terms_version");
  const isPending = bookingStatus === "awaiting_customer_confirmation" && tokenStatus === "active" && !expired;

  // onAction: same sandboxed-demo hook as the rest of this page, when
  // supplied, replaces the real accept-changes/request-clarification POSTs
  // so this card can safely render fabricated version_diff data.
  const accept = async () => {
    setBusy(true);
    try {
      if (onAction) onAction("accept_changes", { terms_acknowledged: termsAcknowledged });
      else await api.post(`/public/confirm/${token}/accept-changes`, { terms_acknowledged: termsAcknowledged });
      toast.success("Thank you: changes accepted.");
      onAccepted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const requestClarification = async () => {
    if (!msg.trim()) {
      toast.error("Please describe what you'd like to clarify.");
      return;
    }
    setBusy(true);
    try {
      if (onAction) onAction("request_clarification", { message: msg });
      else await api.post(`/public/confirm/${token}/request-clarification`,
                          { action: "request_change", message: msg });
      toast.success("Your message was sent to the business.");
      setShowAsk(false); setMsg("");
      onClarificationRequested?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5"
         data-testid="pending-change-card">
      <div className="font-display text-lg font-semibold text-amber-900">
        The rental business updated your booking details
      </div>
      <p className="text-sm text-amber-900/80 mt-1">
        Please review the changes before pickup.
      </p>

      <div className="mt-4 space-y-3">
        {rows.map((f) => (
          <div key={f.key} className="grid sm:grid-cols-2 gap-3 text-sm" data-testid={`pending-change-row-${f.key}`}>
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="pending-change-old">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Previous {f.label}</div>
              <div className="font-medium mt-0.5">{f.get(versionDiff.previous_confirmed_snapshot)}</div>
            </div>
            <div className="rounded-lg border-2 border-[#1B4332] bg-white p-3" data-testid="pending-change-new">
              <div className="text-[10px] uppercase tracking-widest text-[#1B4332]">Updated {f.label}</div>
              <div className="font-medium mt-0.5">{f.get(versionDiff.current_snapshot)}</div>
            </div>
          </div>
        ))}
        {termsChanged && (
          <div className="grid sm:grid-cols-2 gap-3 text-sm" data-testid="pending-change-row-terms_version">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Previous rental terms</div>
              <div className="font-medium mt-0.5">
                {versionDiff.previous_terms_version_number ? `Version ${versionDiff.previous_terms_version_number}` : "-"}
              </div>
            </div>
            <div className="rounded-lg border-2 border-[#1B4332] bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#1B4332]">Updated rental terms</div>
              <div className="font-medium mt-0.5">
                {versionDiff.terms_version_number ? `Version ${versionDiff.terms_version_number}` : "-"}
              </div>
            </div>
          </div>
        )}
      </div>

      {isPending && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={accept} disabled={busy || termsGateBlocked}
                  data-testid="pending-change-accept-btn"
                  title={termsGateBlocked ? "Please acknowledge the booking terms above first" : ""}
                  className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm hover:opacity-90 disabled:opacity-50">
            Accept changes
          </button>
          <button onClick={() => setShowAsk((v) => !v)} disabled={busy}
                  data-testid="pending-change-request-btn"
                  className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50">
            Request clarification
          </button>
        </div>
      )}
      {showAsk && (
        <div className="mt-3" data-testid="pending-change-ask-form">
          <textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)}
                    placeholder="What would you like to clarify with the business?"
                    data-testid="pending-change-message"
                    className="w-full text-sm rounded-md border border-slate-300 p-2" />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setShowAsk(false); setMsg(""); }}
                    className="px-3 py-1.5 text-sm rounded-md hover:bg-slate-100">Cancel</button>
            <button onClick={requestClarification} disabled={busy}
                    data-testid="pending-change-send-msg"
                    className="px-3 py-1.5 text-sm rounded-md bg-[#1B4332] text-white hover:opacity-90 disabled:opacity-50">
              Send message
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Customer-safe timeline
// -----------------------------------------------------------------------
function CustomerTimelineCard({ token, disabled = false }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (disabled) { setLoaded(true); return; }
    (async () => {
      try {
        const { data } = await api.get(`/public/confirm/${token}/timeline`);
        setItems(data.items || []);
      } catch (_) { /* hide on error */ }
      finally { setLoaded(true); }
    })();
  }, [token, disabled]);

  if (!loaded || items.length === 0) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5"
         data-testid="customer-timeline-card">
      <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-[#1B4332]" /> Your booking timeline
      </h3>
      <ol className="mt-3 relative border-l border-slate-200 ml-2 pl-4 space-y-2"
          data-testid="customer-timeline-list">
        {items.map((e) => (
          <li key={e.id} className="text-sm relative" data-testid={`customer-timeline-${e.id}`}>
            <span className="absolute -left-[22px] top-1.5 w-2 h-2 rounded-full bg-[#1B4332]" />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="font-medium text-slate-900">{e.label}</span>
              <span className="text-xs text-slate-500">
                {e.at ? new Date(e.at).toLocaleString() : ""}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

